import { useState, useEffect } from "react";
import { IconCheck, IconAlertTriangle } from "@tabler/icons-react";
import { ROLE_OPTIONS } from "./data";
import Input, { Select } from "../UI/FormInput/input";
import ModalShell from "../Support/ModalShell";
import Button from "../UI/Button/button";
import { getTeamApiError } from "./utils/apiError";
import { diffFields } from "../../utils/functions";

const rolesFor = (currentUserRole) =>
  currentUserRole === "Manager"
    ? ROLE_OPTIONS.filter((r) => r === "Member" || r === "Viewer")
    : ROLE_OPTIONS;

function toFormState(user) {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    role: user.role,
    // "" is our sentinel for "no team", mapped to "N/A" on submit.
    teamId: user.teamId?._id || user.teamId || "",
  };
}

export default function EditUserModal({
  open,
  user,
  teams,
  currentUserRole,
  currentUserId,
  onClose,
  onSave,
}) {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      const state = toFormState(user);
      setInitial(state);
      setForm(state);
      setError(null);
    }
  }, [user]);

  if (!user || !form) return null;

  const isSelf = user?._id === currentUserId;
  const roleLocked = isSelf || user?.isOwner;

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    const changed = diffFields(initial, form);
    if ("teamId" in changed) {
      // API accepts "N/A" or "" for "no team" — either works, using
      // "N/A" to match the doc's example verbatim.
      changed.teamId = changed.teamId === "" ? "N/A" : changed.teamId;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSave(user?._id, changed);
    } catch (err) {
      setError(getTeamApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Edit User"
      footer={
        <>
          <Button variant="outlineBlue" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<IconCheck size={15} />}
            onClick={handleSubmit}
            loading={submitting}
          >
            Save Changes
          </Button>
        </>
      }
    >
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <IconAlertTriangle
            size={15}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <span>
            {typeof error === "string"
              ? error
              : "Please check the form and try again."}
          </span>
        </div>
      )}

      <Input
        label="First Name"
        id="editUserFirstName"
        placeholder="First name"
        value={form.firstName}
        onChange={setField("firstName")}
      />
      <Input
        label="Last Name"
        id="editUserLastName"
        placeholder="Last name"
        value={form.lastName}
        onChange={setField("lastName")}
      />
      <Input
        label="Email Address"
        id="editUserEmail"
        type="email"
        placeholder="email@company.com"
        value={form.email}
        onChange={setField("email")}
      />
      <Select
        label="Role"
        id="editUserRole"
        value={form.role}
        onChange={setField("role")}
        disabled={roleLocked}
        hint={
          isSelf
            ? "You can't change your own role."
            : user.isOwner
              ? "The account owner must remain Admin."
              : undefined
        }
      >
        {rolesFor(currentUserRole)?.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
        {!rolesFor(currentUserRole)?.includes(form?.role) && (
          <option value={form?.role}>{form?.role}</option>
        )}
      </Select>
      <Select
        label="Team"
        id="editUserTeam"
        value={form.teamId}
        onChange={setField("teamId")}
      >
        <option value="">No team</option>
        {teams?.map((team) => (
          <option key={team?._id} value={team?._id}>
            {team?.name}
          </option>
        ))}
      </Select>
    </ModalShell>
  );
}
